import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { OctroGoldCoin } from 'components/OctroGoldCoin';
import {
  displayLayoutForSeat,
  betChipPosition,
  seatChipFlyOrigin,
  isValidFeltSeatNo,
  potZoneCoord,
  type SeatLayoutProfile,
  type SeatPosition,
} from 'constants/seatLayout';
import { useCurrency } from 'hooks/useCurrency';

interface ChipFlyAnimationProps {
  fromSeatNo: number;
  seatLayoutMap: Map<number, SeatPosition>;
  seatProfile?: SeatLayoutProfile;
  amount: number;
  onDone?: () => void;
  onLaunch?: () => void;
  silent?: boolean;
  /** Chip SFX on launch when silent (boot ante — hides amount label only) */
  withSound?: boolean;
}

export default function ChipFlyAnimation({
  fromSeatNo,
  seatLayoutMap,
  seatProfile = 'desktop',
  amount,
  onDone,
  onLaunch,
  silent,
  withSound,
}: ChipFlyAnimationProps) {
  const { symbol, format } = useCurrency();
  const [visible, setVisible] = useState(true);
  const layout = displayLayoutForSeat(fromSeatNo, seatLayoutMap, seatProfile);
  const seatNo = layout?.seatNumber ?? fromSeatNo;
  const from =
    layout && isValidFeltSeatNo(seatNo)
      ? silent
        ? seatChipFlyOrigin(seatNo, seatProfile)
        : betChipPosition(layout)
      : null;
  const onDoneRef = useRef(onDone);
  const onLaunchRef = useRef(onLaunch);

  useEffect(() => {
    onDoneRef.current = onDone;
  }, [onDone]);

  useEffect(() => {
    onLaunchRef.current = onLaunch;
  }, [onLaunch]);

  useEffect(() => {
    if (!from) {
      onDoneRef.current?.();
      return;
    }
    let cancelled = false;
    if (!silent || withSound) onLaunchRef.current?.();
    const t = window.setTimeout(() => {
      if (cancelled) return;
      setVisible(false);
      onDoneRef.current?.();
    }, 820);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [silent, withSound, from]);

  if (amount <= 0 || !from || !layout) return null;

  const potTarget = potZoneCoord(seatProfile);
  const chipCount = Math.min(3, Math.max(1, Math.ceil(amount / 30)));

  const amountLabel =
    amount >= 1000 ? `+${symbol}${(amount / 1000).toFixed(1)}k` : `+${format(amount)}`;

  return (
    <AnimatePresence>
      {visible && (
        <>
          {Array.from({ length: chipCount }).map((_, i) => (
            <motion.div
              key={`${fromSeatNo}-${i}-${amount}`}
              className="pointer-events-none absolute z-[45]"
              initial={{
                left: `${from.x}%`,
                top: `${from.y}%`,
                x: '-50%',
                y: '-50%',
                opacity: 0,
                scale: 0.4,
              }}
              animate={{
                left: `${potTarget.x}%`,
                top: `${potTarget.y}%`,
                opacity: [0, 1, 0.85, 0],
                scale: [0.4, 1.08, 0.95, 0.45],
              }}
              exit={{ opacity: 0 }}
              transition={{
                duration: 0.72,
                delay: i * 0.09,
                ease: [0.25, 0.46, 0.45, 0.94],
              }}
            >
              <OctroGoldCoin size={24} />
            </motion.div>
          ))}
          {!silent && (
            <motion.span
              key={`${fromSeatNo}-amt-${amount}`}
              className="tp-chip-fly-amount tp-chip-fly-amount-seat pointer-events-none absolute z-[50]"
              initial={{
                left: `${from.x}%`,
                top: `${from.y}%`,
                x: '-50%',
                y: '-50%',
                opacity: 0,
                scale: 0.82,
              }}
              animate={{
                opacity: [0, 1, 1, 0],
                y: ['-50%', '-72%', '-88%', '-100%'],
                scale: [0.82, 1.08, 1, 0.94],
              }}
              transition={{ duration: 0.82, ease: [0.22, 0.55, 0.35, 1] }}
            >
              {amountLabel}
            </motion.span>
          )}
        </>
      )}
    </AnimatePresence>
  );
}
